import React from "react";

const EmailIcon = () => <span className="material-icons text-[20px]">email</span>;
const AddressIcon = () => <span className="material-icons text-[20px]">location_on</span>;
const WebsiteIcon = () => <span className="material-icons text-[20px]">public</span>;

export default function PrivacyPolicy() {
  return (
    <div className="border p-6 bg-[#F1F1F1] shadow-sm">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold">Privacy Policy</h1>
          <p className="mt-2 text-[#777B7F]">
            Learn how we collect, use, and protect your personal data.
          </p>
        </div>
      </div>

      {/* Introduction */}
      <div className="mt-6 p-4 bg-white rounded-lg shadow-sm">
        <p className="text-sm text-[#777B7F] mt-2">
          Roott is committed to protecting your privacy and complying with the UK General Data Protection Regulation (UK GDPR) and the Data Protection Act 2018. If you have any questions, please contact us at{" "}
          <a href="mailto:team@roott.one" className="text-[#93C893] hover:underline">
            team@roott.one
          </a>
        </p>
      </div>

      {/* Privacy Content */}
      <div className="mt-6 bg-white rounded-lg shadow-sm p-6">
        <div className="prose max-w-none text-[#777B7F] space-y-6">
          <section>
            <h2 className="text-lg font-semibold text-gray-800 mb-3">1. Purpose of This Policy</h2>
            <p>
              This Privacy Policy explains how Roott Ltd ("Roott," "we," "our," or "us") collects, uses, stores, and protects your personal data when you visit www.roott.one or use our online platform ("Service").
            </p>
            <p>
              Roott is committed to protecting your privacy and complying with the UK General Data Protection Regulation (UK GDPR) and the Data Protection Act 2018.
            </p>
          </section>

          <section>
            <h2 className="text-lg font-semibold text-gray-800 mb-3">2. Data We Collect</h2>
            <p>
              We collect only the information necessary to operate and improve our Service:
            </p>
            <p className="font-semibold text-gray-800 mt-4">a. Information You Provide</p>
            <p>
              Name, email address, and contact details when creating an account; organization name and role; payment information (processed securely by our payment partner, e.g., Paddle or Stripe); content you upload (e.g., grant drafts, notes, attachments); and messages or inquiries sent to our support team at team@roott.one.
            </p>
            <p className="font-semibold text-gray-800 mt-4">b. Information We Automatically Collect</p>
            <p>
              When you use Roott, we automatically collect log and usage data (pages visited, features used, session duration), device and browser information, IP address and approximate location (for security and analytics), and cookies and similar tracking technologies (see Section 10).
            </p>
            <p className="font-semibold text-gray-800 mt-4">c. AI-Generated and Processed Data</p>
            <p>
              Our AI tools process text that you input to generate draft applications or funding suggestions. This data may be temporarily stored to provide outputs, but we do not use your private data to train external AI models.
            </p>
          </section>

          <section>
            <h2 className="text-lg font-semibold text-gray-800 mb-3">3. How We Use Your Information</h2>
            <p>
              We use your information to provide and improve our platform and its features, personalize your experience and recommend relevant funding opportunities, process payments and manage your subscription, communicate with you about your account, billing, or updates, detect, prevent, and address fraud or technical issues, and comply with legal obligations.
            </p>
            <p>
              We will never sell your personal data.
            </p>
          </section>

          <section>
            <h2 className="text-lg font-semibold text-gray-800 mb-3">4. Legal Basis for Processing</h2>
            <p>
              Under the UK GDPR, we rely on the following lawful bases: contractual necessity (to provide the Service you sign up for), legitimate interest (to improve features and prevent misuse), consent (for marketing emails or cookies), and legal obligation (to comply with tax, accounting, and regulatory requirements).
            </p>
          </section>

          <section>
            <h2 className="text-lg font-semibold text-gray-800 mb-3">5. How We Share Information</h2>
            <p>
              We may share limited data with payment processors (Paddle, Stripe, or Lemon Squeezy) for billing, hosting and analytics providers (e.g., AWS, Google Analytics, OpenAI API), and compliance and legal partners if required by law or court order.
            </p>
            <p>
              All third-party providers are GDPR-compliant and bound by strict data-processing agreements. We do not sell or rent your data to advertisers or other third parties.
            </p>
          </section>

          <section>
            <h2 className="text-lg font-semibold text-gray-800 mb-3">6. International Data Transfers</h2>
            <p>
              Some service providers operate outside the UK/EEA (e.g., U.S.). Where data is transferred internationally, we ensure appropriate safeguards such as Standard Contractual Clauses (SCCs) approved by the UK ICO, or binding corporate rules or equivalent protections.
            </p>
          </section>

          <section>
            <h2 className="text-lg font-semibold text-gray-800 mb-3">7. Data Retention</h2>
            <p>
              We keep your information only as long as needed to maintain your account and provide the Service, and to comply with legal or accounting obligations.
            </p>
            <p>
              When your account is deleted or inactive for more than 24 months, we securely erase or anonymize your data.
            </p>
          </section>

          <section>
            <h2 className="text-lg font-semibold text-gray-800 mb-3">8. Your Rights</h2>
            <p>
              Under the UK GDPR, you have the right to access the data we hold about you, correct inaccurate or outdated information, request deletion ("right to be forgotten"), object to processing or withdraw consent, and request data portability.
            </p>
            <p>
              To exercise these rights, email us at team@roott.one. We will respond within 30 days.
            </p>
          </section>

          <section>
            <h2 className="text-lg font-semibold text-gray-800 mb-3">9. Data Security</h2>
            <p>
              We take security seriously. Measures include encrypted data transmission (HTTPS/TLS), secure cloud infrastructure with restricted access, regular vulnerability scans and backups, and limited employee access to personal data.
            </p>
            <p>
              However, no online service can guarantee absolute security. Use strong passwords and keep your credentials confidential.
            </p>
          </section>

          <section>
            <h2 className="text-lg font-semibold text-gray-800 mb-3">10. Cookies and Tracking</h2>
            <p>
              Roott uses cookies and similar technologies to remember your login preferences, improve platform performance, and analyse anonymous usage statistics.
            </p>
            <p>
              You can adjust or reject cookies via your browser settings. For full details, see our Cookie Policy at www.roott.one/cookies (coming soon).
            </p>
          </section>

          <section>
            <h2 className="text-lg font-semibold text-gray-800 mb-3">11. AI Data Handling Transparency</h2>
            <p>
              Roott's AI tools use secure APIs (e.g., OpenAI or similar) to generate draft content. Input text is processed only for generating your requested output and is not stored beyond session context, except where saved in your account. We do not use customer data to retrain third-party models.
            </p>
            <p>
              If you choose to delete a draft or your account, all stored AI outputs and inputs linked to your profile are permanently removed.
            </p>
          </section>

          <section>
            <h2 className="text-lg font-semibold text-gray-800 mb-3">12. Children's Data</h2>
            <p>
              Our Service is intended for users aged 18 and over. We do not knowingly collect data from minors. If you believe a child has provided us data, contact team@roott.one for immediate deletion.
            </p>
          </section>

          <section>
            <h2 className="text-lg font-semibold text-gray-800 mb-3">13. Marketing Communications</h2>
            <p>
              With your consent, we may send updates about Roott, funding insights, or new features. You can unsubscribe anytime by clicking "unsubscribe" in the email or contacting us directly.
            </p>
          </section>

          <section>
            <h2 className="text-lg font-semibold text-gray-800 mb-3">14. Updates to This Policy</h2>
            <p>
              We may update this Privacy Policy from time to time. Changes will be posted at www.roott.one/privacy with a revised effective date.
            </p>
            <p>
              Continued use of the Service after updates means you accept the revised policy.
            </p>
          </section>

          <section>
            <h2 className="text-lg font-semibold text-gray-800 mb-3">15. Contact Us</h2>
            <p>If you have any questions, concerns, or data requests, please contact:</p>
            <p className="mt-2 space-y-1">
              <span className="flex items-center gap-2">
                <EmailIcon /><b> Email:</b> <a href="mailto:team@roott.one">team@roott.one</a>
              </span>
              <span className="flex items-center gap-2">
                <AddressIcon /> <b>Address:</b> Roott Ltd, 26 Waterside Court, Millpond Place, Carshalton, SM5 2JT, United Kingdom
              </span>
              <span className="flex items-center gap-2">
                <WebsiteIcon /> <b>Website:</b> <a href="https://www.roott.one" target="_blank" rel="noopener noreferrer">www.roott.one</a>
              </span>
            </p>
          </section>
        </div>
      </div>

      {/* Footer Contact Section */}
      {/* <div className="mt-8 p-6 bg-white rounded-lg shadow-sm">
        <h3 className="text-lg font-semibold mb-4">Need Help?</h3>
        <p className="text-[#777B7F] mb-4">
          If you have any questions about our Privacy Policy or how we handle your data, please don't hesitate to reach out to our team.
        </p>
        <div className="flex flex-col sm:flex-row gap-3">
          <a
            href="mailto:team@roott.one"
            className="bg-[#93C893] px-6 py-2 rounded-lg hover:opacity-90 text-center"
          >
            Email Support
          </a>
          <a
            href="https://www.roott.one"
            target="_blank"
            rel="noopener noreferrer"
            className="border border-gray-300 px-6 py-2 rounded-lg hover:bg-gray-50 text-center"
          >
            Visit Website
          </a>
        </div>
      </div> */}
    </div>
  );
}